/**
 * Runtime schémata médií (zod). Přestěhovaná z `types/media.ts` (řez 13, Kontrakt 4(a)),
 * aby `types/**` za běhu na zod nedosáhl: klientský kód webu bere typy a konstanty přes
 * `@ckgo2/shared/client` nebo `/types` a validaci nepotřebuje. Typy stejných jmen
 * (`MediaResponse`, `MediaCreate`, …) zůstávají v `types/media.ts`.
 */
import { z } from "zod";
import { FOCAL_SOURCES } from "../media/focal";
import { MEDIA_ORIENTATIONS } from "../media/orientation";
import {
  AI_REFRESH_BATCH_MAX_IDS,
  ASSIGN_COUNTRIES_BATCH_MAX_IDS,
  MERGE_BATCH_MAX_HASHES,
} from "../media/batches";
import { MEDIA_HASH_CHECK_MAX } from "../media/upload";
import { API_ERROR } from "../apiErrors";
import { MEDIA_FOLDERS } from "../types/media";

export const FocalSourceSchema = z.enum(FOCAL_SOURCES);

/** Strop počtu zemí jedné fotky i jedné hromadné akce. */
export const MEDIA_COUNTRY_IDS_MAX = 60;

/** Zdroj fotky: volný text do 120 znaků po ořezu, `null` = bez zdroje. */
function mediaSourceValueSchema() {
  return z.string().trim().max(120).nullable();
}

/** Seznam ID zemí (destinací zemské úrovně), `min`–`MEDIA_COUNTRY_IDS_MAX` položek. */
function mediaCountryIdsSchema(min: number) {
  return z.array(z.string().min(1)).min(min).max(MEDIA_COUNTRY_IDS_MAX);
}

export const MediaFolderSchema = z.enum(MEDIA_FOLDERS);

/**
 * Media as returned by the API. `url`/`thumbUrl` are derived server-side
 * (CDN + cdn-cgi/image transform), not stored in the DB.
 */
export const MediaSchema = z.object({
  id: z.string(),
  r2Key: z.string(),
  r2KeyThumb: z.string(),
  r2KeyOriginal: z.string().nullable(),
  alt: z.string(),
  title: z.string().nullable(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  sizeBytes: z.number().int().nonnegative(),
  mimeType: z.string(),
  originalName: z.string().nullable(),
  folder: MediaFolderSchema,
  focalPointX: z.number().min(0).max(1),
  focalPointY: z.number().min(0).max(1),
  /** Režim ořezu — viz `packages/shared/src/media/focal.ts`. */
  focalSource: FocalSourceSchema,
  blurDataUrl: z.string().nullable(),
  tags: z.array(z.string()),
  description: z.string().nullable().optional(),
  uploadedAt: z.string(),
  uploadedBy: z.string().nullable(),
  deletedAt: z.string().nullable().optional(),
  // Derived (built API-side):
  url: z.string(),
  thumbUrl: z.string(),
});

export const MediaCreateSchema = z.object({
  alt: z.string().min(1, API_ERROR.media_alt_required).max(125),
  title: z.string().max(200).optional(),
  folder: MediaFolderSchema,
  focalPointX: z.number().min(0).max(1).default(0.5),
  focalPointY: z.number().min(0).max(1).default(0.5),
  tags: z.array(z.string()).default([]),
  description: z.string().max(500).nullable().optional(),
});

/**
 * Režim ořezu nemá Create (při nahrání ho určuje přítomnost souřadnic v těle), ale Update
 * ano: je to JEDINÝ signál, který režim mění. Tělo bez `focalSource` režim nechává, i když
 * nese souřadnice — admin je přikládá ke každému uložení zásuvky. Tělo smí poslat jen
 * `auto` („Zpět na automatický“, s AI bodem z něj server udělá `ai`) a `manual`; stav `ai`
 * nastavuje jen server (`applyFocalPatch`, `applyAiFocal`).
 */
export const MediaUpdateSchema = MediaCreateSchema.partial().extend({
  focalSource: z.enum(["auto", "manual"]).optional(),
  /** Zdroj fotky; prázdný řetězec API uloží jako `null`. */
  source: mediaSourceValueSchema().optional(),
  isStarred: z.boolean().optional(),
  /** NAHRADÍ množinu zemí fotky; jen destinace zemské úrovně (hlídá API). */
  countryIds: mediaCountryIdsSchema(0).optional(),
});

/**
 * Médium, jak ho vrací ADMIN (`GET/PATCH /api/admin/upload/:id`, seznam stránky Média).
 * Veřejné projekce dál nesou jen `MediaSchema`: zdroj se na webu nepodepisuje a ★, země
 * ani počet použití web nepotřebuje (vize podnety-tymu-2026-10, AK14).
 */
export const AdminMediaSchema = MediaSchema.extend({
  source: z.string().nullable(),
  isStarred: z.boolean(),
  /** ID destinací zemské úrovně z `media_countries`. */
  countryIds: z.array(z.string()),
  /** Počet míst použití v indexu (`apps/api/src/lib/mediaUsage.ts`). */
  usageCount: z.number().int().nonnegative(),
  /** Jen u fotky v koši, jejíž otisk má živá fotka: ID té živé fotky; jinak `null`. */
  liveTwinId: z.string().nullable(),
  /** SHA-256 hex nahraného souboru (přesná duplicita); `null` u fotek bez otisku. */
  contentHash: z.string().nullable(),
  /** SHA-256 hex PŮVODNÍHO souboru, když ho prohlížeč před odesláním zmenšil; jinak `null`. */
  originalContentHash: z.string().nullable(),
  /** AI bod (hlavní motiv podle modelu, 0..1); `null` = AI bod nemá. Jen admin, web ho nečte. */
  aiFocalPointX: z.number().min(0).max(1).nullable(),
  aiFocalPointY: z.number().min(0).max(1).nullable(),
});

/** Parametry `GET /api/admin/upload` (stránka Média i výběr fotek); vše nepovinné. */
export const MediaListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  /** Druh (pevná kategorie `media.folder`). */
  folder: MediaFolderSchema.optional(),
  /** Album (`media_folders.id`). */
  folderId: z.string().min(1).optional(),
  /** ID destinace zemské úrovně (zahrne podstrom), `"none"` = bez země, `"any"` = aspoň jedna. */
  country: z.string().min(1).optional(),
  starred: z.literal("1").optional(),
  /** Přesná shoda zdroje. */
  source: z.string().min(1).max(120).optional(),
  orientation: z.enum(MEDIA_ORIENTATIONS).optional(),
  usage: z.enum(["used", "unused"]).optional(),
  /** Pořadí výběru: `"starred"` = ★ napřed, pak nejnovější (okno výběru fotek posílá vždy,
   *  stránka Média nikdy); bez něj dnešní pořadí seznamu. */
  sort: z.enum(["starred"]).optional(),
  /** `"true"` = JEN fotky v koši (jako dnes); bez něj jen živé. */
  includeTrashed: z.literal("true").optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Offset jako dnes (`nextCursor` předchozí stránky). */
  cursor: z.coerce.number().int().nonnegative().default(0),
});

/**
 * Hromadné akce stránky Média (`POST /api/admin/upload/bulk`). `ids` bez horní meze: mez
 * dává strop těla požadavku (1 MiB, asi 49 000 ID); D1 strop parametrů řeší API po dávkách.
 * Všechny hromadné zápisy lišty jdou tudy (`trash` = smazání do koše, `setFolder` = přesun
 * druhu); přidání do alba má svou trasu alb (`/api/admin/media-folders`).
 */
const bulkIds = z.array(z.string().min(1)).min(1);
export const MediaBulkActionSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("addCountries"),
    ids: bulkIds,
    countryIds: mediaCountryIdsSchema(1),
  }),
  z.object({
    action: z.literal("removeCountries"),
    ids: bulkIds,
    countryIds: mediaCountryIdsSchema(1),
  }),
  z.object({ action: z.literal("setSource"), ids: bulkIds, source: mediaSourceValueSchema() }),
  z.object({ action: z.literal("setStarred"), ids: bulkIds, isStarred: z.boolean() }),
  /** Měkké smazání do koše (`deletedAt` = čas požadavku, jeden pro celý výběr). */
  z.object({ action: z.literal("trash"), ids: bulkIds }),
  /** Přesun do druhu (`media.folder`). */
  z.object({ action: z.literal("setFolder"), ids: bulkIds, folder: MediaFolderSchema }),
  /**
   * Převzetí existujících fotek do dávky nahrávání (řez 04, F7.9): země dávky se PŘIDAJÍ
   * (sjednocení, nikdy se neodebírají; prázdné pole = nic), zdroj dávky se zapíše jen
   * fotkám bez zdroje (`source IS NULL`); `null` = zdroj se nemění.
   */
  z.object({
    action: z.literal("adoptFromBatch"),
    ids: bulkIds,
    countryIds: mediaCountryIdsSchema(0),
    source: mediaSourceValueSchema(),
  }),
]);

// ── Hromadné nahrávání (řez 04) ──

/** SHA-256 hex malými písmeny (64 znaků). */
export const Sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);

/** `POST /api/admin/upload/check-hashes`: 1 až `MEDIA_HASH_CHECK_MAX` otisků (panel dělí po částech). */
export const MediaHashCheckSchema = z.object({
  hashes: z.array(Sha256HexSchema).min(1).max(MEDIA_HASH_CHECK_MAX),
});

// ── Nástroje knihovny (`/api/admin/backfill-media/*`, řez 08) ──

/** Otisky skupin přesných duplicit jedné dávky sloučení (1 až `MERGE_BATCH_MAX_HASHES`). */
const MergeHashes = z.array(z.string().min(1)).min(1).max(MERGE_BATCH_MAX_HASHES);
/** Zápis `merge-duplicates?dry=false`: `hashes` povinné, slučují se jen skupiny těch otisků. */
export const MergeDuplicatesApplySchema = z.object({ hashes: MergeHashes });
/** Náhled `merge-duplicates?dry=true`: tělo nepovinné, `{}` = celá knihovna. */
export const MergeDuplicatesPreviewSchema = z.object({ hashes: MergeHashes.optional() });
/** `POST assign-countries`: země fotek podle míst použití; bez `dry` jen náhled. */
export const AssignCountriesSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(ASSIGN_COUNTRIES_BATCH_MAX_IDS),
  dry: z.boolean().default(true),
});

/**
 * `POST /api/admin/backfill-media/ai-refresh`: opakování AI ohniska (`focal`) a AI textů
 * (`texts`) nad 1 až `AI_REFRESH_BATCH_MAX_IDS` ID; `dry: true` vrátí stavy bez zápisu.
 * Tvar odpovědi `AiRefreshResponse` (`types/media.ts`).
 */
export const AiRefreshSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(AI_REFRESH_BATCH_MAX_IDS),
  focal: z.boolean(),
  texts: z.boolean(),
  dry: z.boolean().default(false),
});
